import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  CREATE_SUB_ACCOUNT,
  GET_ALL_SUB_ACCOUNTS,
  GET_CURRENCY_HASHRATE_CHART,
  GET_PORTFOLIO_INCOME_HISTORY,
  GET_PORTFOLIO_SUMMARY,
  GET_STATISTICS,
  GET_SUB_ACCOUNT_CHART,
  GET_SUB_ACCOUNT_EARNINGS,
  GET_SUB_ACCOUNT_PAYOUT,
  GET_SUB_ACCOUNT_STRATUM,
  GET_SUB_ACCOUNT_SUMMARY,
  GET_SUB_ACCOUNT_WORKERS,
  UPDATE_SUB_ACCOUNT_WALLET,
} from "@/store/sub_accounts/variables";
import {
  createSubAccount,
  editSubAccountWallet,
  getCurrencyHashrateChart,
  getPortfolioIncomeHistory,
  getPortfolioSummary,
  getStatistics,
  getSubAccountChart,
  getSubAccounts,
  getSubAccountsEarning,
  getSubAccountsPayouts,
  getSubAccountsSummary,
  getSubAccountStratumUrls,
  getSubAccountsWorkers,
} from "@/store/sub_accounts/api";
import {
  CurrencyHashrateChartDto,
  PortfolioIncomeHistoryDto,
  PortfolioSummaryDto,
  StatisticResponseDto,
  SubAccountCreateBodyDto,
  SubAccountSummaryDto,
  SubAccountWorkerDto,
} from "@/models";

export interface IApiKey {
  loading: boolean;
  stratumUrls: Array<any>;
  subAccounts: Array<any>;
  earnings: Array<any>;
  workers: Array<any>;
  allWorkers: Array<any>;
  subAccountChart: {
    hashrates: number[];
    now: any;
  };
  subAccountSummary: SubAccountSummaryDto[];
  subAccountPayout: any;
  dialogs: { [key in string]: boolean };
  subAccountWorkers: SubAccountWorkerDto[];
  portfolioSummary: PortfolioSummaryDto | null;
  portfolioSummaryFailed: boolean;
  portfolioIncomeHistory: PortfolioIncomeHistoryDto | null;
  currencyHashrateChart: CurrencyHashrateChartDto;
  showModal: boolean;
  statistics: StatisticResponseDto[];
  currentSubAccountInfo: any;
  currentSubAccount: string;
  // Screens 1/2/3 navigation (currency + sub-account sidebar) - separate from
  // currentSubAccount, which other cabinet pages (earnings/payouts/stratum)
  // already depend on and this must not disturb.
  statisticsCurrency: string;
  statisticsViewMode: "portfolio" | "aggregate" | "single";
  // Sub-accounts ticked in the "Просмотр" tree, all of statisticsCurrency.
  // Empty means every sub-account of that currency ("Все BTC").
  statisticsSelection: string[];
}
const initialState: IApiKey = {
  loading: false,
  stratumUrls: [],
  subAccounts: [],
  subAccountChart: { hashrates: [], now: "" },
  subAccountSummary: [],
  allWorkers: [],
  workers: [],
  earnings: [],
  dialogs: {},
  statistics: [],
  showModal: false,
  currentSubAccountInfo: null,
  subAccountPayout: [],
  subAccountWorkers: [],
  portfolioSummary: null,
  portfolioSummaryFailed: false,
  portfolioIncomeHistory: null,
  currencyHashrateChart: { times: [], values: [], charted: 0, total: 0 },
  currentSubAccount: "",
  statisticsCurrency: "",
  statisticsViewMode: "portfolio",
  statisticsSelection: [],
};

const getAllSubAccounts$ = createAsyncThunk(GET_ALL_SUB_ACCOUNTS, async () => {
  const response = await getSubAccounts();
  return Promise.resolve(response.data);
});

const getSubAccountSummary$ = createAsyncThunk(
  GET_SUB_ACCOUNT_SUMMARY,
  async (name: string, { signal }) => {
    const response = await getSubAccountsSummary(name, { signal });
    // The backend already includes the real crypto_currency here
    // (SubAccountDetailHandler.get_summary(), sub_account_detail_handler.py) -
    // no need to fill it in on the client.
    const result = response.data as { data: SubAccountSummaryDto };
    return Promise.resolve([result.data]);
  },
);

const getPortfolioSummary$ = createAsyncThunk(
  GET_PORTFOLIO_SUMMARY,
  async (_: void, { signal }) => {
    const response = await getPortfolioSummary({ signal });
    return Promise.resolve(response.data as PortfolioSummaryDto);
  },
);

const getPortfolioIncomeHistory$ = createAsyncThunk(
  GET_PORTFOLIO_INCOME_HISTORY,
  async (_: void, { signal }) => {
    const response = await getPortfolioIncomeHistory({ signal });
    return Promise.resolve(response.data as PortfolioIncomeHistoryDto);
  },
);

const getCurrencyHashrateChart$ = createAsyncThunk(
  GET_CURRENCY_HASHRATE_CHART,
  async (
    body: { currency: string; interval: string; subAccountNames?: string },
    { signal },
  ) => {
    const response = await getCurrencyHashrateChart(body, signal);
    return Promise.resolve(response.data as CurrencyHashrateChartDto);
  },
);

const getSubAccountPayout$ = createAsyncThunk(
  GET_SUB_ACCOUNT_PAYOUT,
  async (params: any, thunkAPI) => {
    const response = await getSubAccountsPayouts(params, {
      signal: thunkAPI.signal,
    });
    return Promise.resolve(response.data);
  },
);

const getSubAccountWorkers$ = createAsyncThunk(
  GET_SUB_ACCOUNT_WORKERS,
  async (
    params: { sub_account_name: string; worker_status: string },
    thunkAPI,
  ) => {
    const response = await getSubAccountsWorkers(params, {
      signal: thunkAPI.signal,
    });
    return Promise.resolve(response.data);
  },
);

const createSubAccount$ = createAsyncThunk(
  CREATE_SUB_ACCOUNT,
  async (body: SubAccountCreateBodyDto) => {
    return createSubAccount(body)
      .then((res: any) => {
        if (res.status === 201 || res.status === 202) {
          return Promise.resolve(res);
        }
        return Promise.resolve(res.data);
      })
      .catch((e) => {
        return Promise.reject(e.response.data?.error_code || 5000);
      });
  },
);

const getSubAccountStratum$ = createAsyncThunk(
  GET_SUB_ACCOUNT_STRATUM,
  async (name: string) => {
    return getSubAccountStratumUrls(name).then((response) => response.data);
  },
);

const getSubAccountEarnings$ = createAsyncThunk(
  GET_SUB_ACCOUNT_EARNINGS,
  async (params: any, { signal }) => {
    const response = await getSubAccountsEarning(params, { signal });
    return Promise.resolve(response.data);
  },
);

const getStatistics$ = createAsyncThunk(GET_STATISTICS, async () => {
  const response = await getStatistics();
  return Promise.resolve(
    ["BTC", "BCH", "LTC"].map(
      (key) => (response.data as any)[key] as StatisticResponseDto,
    ),
  );
});

const getSubAccountChart$ = createAsyncThunk(
  GET_SUB_ACCOUNT_CHART,
  async (body: { sub_account_name: string; interval: string }, { signal }) => {
    const response = await getSubAccountChart(body, signal);
    return Promise.resolve(response.data);
  },
);

const updateSubAccountWallet = createAsyncThunk(
  UPDATE_SUB_ACCOUNT_WALLET,
  async (body: { sub_account_id: number; wallet_address: string }) => {
    return editSubAccountWallet(body)
      .then((res: any) => {
        return Promise.resolve(res.data);
      })
      .catch((e) => {
        return Promise.reject(e.response?.data);
      });
  },
);

export const subAccountSlice = createSlice({
  name: "subaccount",
  initialState,
  reducers: {
    setCurrentSubAccount(state, { payload }) {
      state.currentSubAccount = payload;
    },
    setCurrentSubAccountInfo(state, { payload }) {
      state.currentSubAccountInfo = payload;
    },
    setLoading(state, { payload }) {
      state.loading = payload;
    },
    setShowModal(state, { payload }) {
      state.showModal = payload;
    },
    setWorkersForFilter(state, { payload }) {
      state.workers = [...payload];
    },
    setDialogs(state, { payload }) {
      const { key, value } = payload;
      state.dialogs[key] = value;
      state.dialogs = { ...state.dialogs };
    },
    // Selecting "Все активы" in the sidebar -> Screen 1 (portfolio).
    setStatisticsPortfolio(state) {
      state.statisticsViewMode = "portfolio";
      state.statisticsSelection = [];
    },
    // Selecting a currency group in the new sidebar -> Screen 2 (aggregate).
    setStatisticsCurrency(state, { payload }: { payload: string }) {
      state.statisticsCurrency = payload;
      state.statisticsViewMode = "aggregate";
      state.statisticsSelection = [];
    },
    // Selecting one sub-account in the new sidebar -> Screen 3 (single detail).
    setStatisticsSubAccount(
      state,
      { payload }: { payload: { name: string; currency: string } },
    ) {
      state.currentSubAccount = payload.name;
      state.statisticsCurrency = payload.currency;
      state.statisticsViewMode = "single";
      state.statisticsSelection = [];
    },
    // Ticking a sub-account in the tree -> aggregate of the ticked ones.
    // A selection never mixes currencies: ticking another coin starts over.
    toggleStatisticsSelection(
      state,
      { payload }: { payload: { name: string; currency: string } },
    ) {
      const sameScope =
        state.statisticsViewMode === "aggregate" &&
        state.statisticsCurrency === payload.currency;
      const current = sameScope ? state.statisticsSelection : [];
      state.statisticsSelection = current.includes(payload.name)
        ? current.filter((n) => n !== payload.name)
        : [...current, payload.name];
      state.statisticsCurrency = payload.currency;
      state.statisticsViewMode = "aggregate";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSubAccountEarnings$.fulfilled, (state, action) => {
      state.earnings = action.payload as any;
      state.earnings = state.earnings.sort(
        (current, prev) =>
          new Date(prev.date).getTime() - new Date(current.date).getTime(),
      );
      state.loading = false;
    });
    builder.addCase(
      getAllSubAccounts$.fulfilled,
      (state, { payload }: { payload: any }) => {
        state.subAccounts = payload as any;
        if (payload.length) {
          state.currentSubAccount = payload[0].name;
          state.currentSubAccountInfo = payload[0];
        }
      },
    );
    builder.addCase(getSubAccountSummary$.fulfilled, (state, action) => {
      state.subAccountSummary = action.payload as any;
      state.loading = false;
    });
    builder.addCase(getSubAccountSummary$.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getSubAccountPayout$.fulfilled, (state, action) => {
      state.subAccountPayout = action.payload as any;
      state.loading = false;
    });
    builder.addCase(getSubAccountWorkers$.fulfilled, (state, action) => {
      state.workers = action.payload as any;
      state.allWorkers = action.payload as any;
      state.subAccountWorkers = action.payload as SubAccountWorkerDto[];
      state.loading = false;
    });
    builder.addCase(createSubAccount$.fulfilled, (state, action) => {
      state.subAccounts.push(action.payload);
    });
    builder.addCase(getSubAccountStratum$.fulfilled, (state, action) => {
      state.stratumUrls = action.payload as any;
    });
    builder.addCase(getStatistics$.fulfilled, (state, action) => {
      state.statistics = action.payload as any;
      state.loading = false;
    });
    builder.addCase(getSubAccountChart$.fulfilled, (state, action) => {
      state.subAccountChart = action.payload as any;
    });
    builder.addCase(getSubAccountWorkers$.rejected, (state) => {
      state.subAccountWorkers = [];
    });
    builder.addCase(getPortfolioSummary$.pending, (state) => {
      state.portfolioSummaryFailed = false;
    });
    builder.addCase(getPortfolioSummary$.rejected, (state, action) => {
      // Leaving the page aborts the request; that is not a failure.
      state.portfolioSummaryFailed = !action.meta.aborted;
    });
    builder.addCase(getPortfolioSummary$.fulfilled, (state, action) => {
      state.portfolioSummary = action.payload;
    });
    builder.addCase(getPortfolioIncomeHistory$.fulfilled, (state, action) => {
      state.portfolioIncomeHistory = action.payload;
    });
    builder.addCase(getCurrencyHashrateChart$.fulfilled, (state, action) => {
      state.currencyHashrateChart = action.payload;
    });
    builder.addCase(getCurrencyHashrateChart$.rejected, (state) => {
      state.currencyHashrateChart = { times: [], values: [], charted: 0, total: 0 };
    });
  },
});

export const subAccountReducer = subAccountSlice.reducer;

export const {
  setCurrentSubAccount,
  setLoading,
  setDialogs,
  setCurrentSubAccountInfo,
  setShowModal,
  setWorkersForFilter,
  setStatisticsPortfolio,
  setStatisticsCurrency,
  setStatisticsSubAccount,
  toggleStatisticsSelection,
} = subAccountSlice.actions;

export {
  getAllSubAccounts$,
  updateSubAccountWallet,
  getSubAccountPayout$,
  getSubAccountEarnings$,
  getStatistics$,
  getSubAccountWorkers$,
  createSubAccount$,
  getSubAccountStratum$,
  getSubAccountSummary$,
  getSubAccountChart$,
  getPortfolioSummary$,
  getPortfolioIncomeHistory$,
  getCurrencyHashrateChart$,
};
